"use client"

import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { useAppContext } from "../../../shared/functions/Context"
import useTitle from "../../../shared/hooks/useTitle"
import useBackButton from "../../../shared/hooks/useBack"
import type User from "../../../shared/models/User"
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary"
import EmptyError from "../../admin-settings/EmptyError"
import { LoadingEllipsis } from "../../../shared/components/loading/Loading"
import type { IDisputeUser } from "../DisputeRequests"
import SingleSelect from "../../../shared/components/single-select/SingleSelect"
import { useNavigate } from "react-router-dom"
import {
  FileText,
  UserIcon,
  Briefcase,
  Building2,
  Calendar,
  ArrowRight,
  Search,
  CheckCircle,
  Clock,
} from "lucide-react"

const DisputeLog = observer(() => {
  const { api, store, ui } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const navigate = useNavigate()

  const measures = store.measure.all
  const departments = store.department.all
  const allScorecards = store.individualScorecard.all

  useTitle("Dispute Log")
  useBackButton()

  const onSearch = (employeeUid: string): void => setSearchValue(employeeUid)

  const sortByName = (a: User, b: User) => {
    return (a.asJson.displayName || "").localeCompare(b.asJson.displayName || "")
  }

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find((dept) => dept.asJson.id === departmentId)
    return department ? department.asJson.name : "Unknown Department"
  }

  const getDisputeUsers = (userIds: string[]): IDisputeUser[] => {
    const disputedScorecards = allScorecards.filter(
      (scorecard) => scorecard.asJson.isDispute && userIds.includes(scorecard.asJson.uid),
    )

    const userDisputeDetails: IDisputeUser[] = disputedScorecards.map((scorecard) => {
      const userId = scorecard.asJson.uid
      const disputedMeasuresCount = measures.filter(
        (measure) => measure.asJson.isDispute && measure.asJson.uid === scorecard.asJson.uid,
      ).length

      const user = store.user.all.find((user) => user.asJson.uid === userId)
      const userName = user ? user.asJson.displayName : "Unknown User"
      const userDepartment = user ? getDepartmentName(user.asJson.department) : "Unknown Department"
      const jobTitle = user ? user.asJson.jobTitle : "Unknown Title"

      return {
        userId,
        userName,
        disputedMeasuresCount,
        jobTitle,
        userDepartment,
        submittedOn: scorecard.asJson.submittedOn,
      }
    })

    return userDisputeDetails
  }

  const options = store.user.all.sort(sortByName).map((user) => {
    return { label: user.asJson.displayName || "", value: user.asJson.uid }
  })

  const [disputeUsers, setDisputeUsers] = useState<IDisputeUser[]>([])

  useEffect(() => {
    const fetchData = async () => {
      const userIds = store.user.all.map((user) => user.asJson.uid)
      const disputeData = getDisputeUsers(userIds)
      setDisputeUsers(disputeData)
      setLoading(false)
    }

    fetchData()
  }, [store.user.all, allScorecards, measures])

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true)
      try {
        await api.individualScorecard.getAll()
        await api.user.getAll()
        await api.department.getAll()
        await api.division.getAll()
        await api.measure.getAll()
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load.",
          type: "danger",
        })
      }
      setLoading(false)
    }
    loadAll()
  }, [api.department, api.division, api.user, api.individualScorecard, api.measure, ui.snackbar])

  const filteredUsers = searchValue ? disputeUsers.filter((user) => user.userId === searchValue) : disputeUsers

  const resolvedCount = measures.filter((m) => m.asJson.isResolved && m.asJson.isDispute).length
  const pendingCount = measures.filter((m) => !m.asJson.isResolved && m.asJson.isDispute).length

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingEllipsis />
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#3f6a8e] mb-2">Dispute Log</h1>
          <p className="text-gray-600">Complete history of all dispute submissions and resolutions</p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-3">
            <Search className="w-5 h-5 text-gray-400" />
            <div className="flex-1">
              <SingleSelect
                name="search-team"
                options={options}
                width="100%"
                onChange={onSearch}
                placeholder="Search by employee name..."
              />
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-[#84cbe9]" />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Total Logged</h3>
            </div>
            <p className="text-3xl font-bold text-[#3f6a8e]">{disputeUsers.length}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <UserIcon className="w-5 h-5 text-[#faa05a]" />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Total Disputes</h3>
            </div>
            <p className="text-3xl font-bold text-[#faa05a]">
              {disputeUsers.reduce((sum, user) => sum + user.disputedMeasuresCount, 0)}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-[#4bb543]" />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Resolved</h3>
            </div>
            <p className="text-3xl font-bold text-[#4bb543]">{resolvedCount}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-100 rounded-lg">
                <Clock className="w-5 h-5 text-[#dc3545]" />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Pending</h3>
            </div>
            <p className="text-3xl font-bold text-[#dc3545]">{pendingCount}</p>
          </div>
        </div>

        {/* Dispute Log Cards */}
        {filteredUsers.length > 0 ? (
          <div className="space-y-4">
            {filteredUsers.map((user) => {
              const userResolvedCount = measures.filter(
                (m) => m.asJson.isResolved && m.asJson.isDispute && m.asJson.uid === user.userId,
              ).length
              const userPendingCount = measures.filter(
                (m) => !m.asJson.isResolved && m.asJson.isDispute && m.asJson.uid === user.userId,
              ).length

              return (
                <div
                  key={user.userId}
                  onClick={() => navigate(`/c/scorecards/resolve/${user.userId}`)}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#84cbe9] to-[#3f6a8e] flex items-center justify-center text-white font-semibold text-lg">
                        {user.userName?.charAt(0) || "?"}
                      </div>

                      {/* User Info */}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[#3f6a8e] mb-1">{user.userName}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            <span>{user.jobTitle}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            <span>{user.userDepartment}</span>
                          </div>
                          {user.submittedOn && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(user.submittedOn).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status Badges */}
                      <div className="flex items-center gap-3">
                        <div className="text-center px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs text-gray-600 mb-1">Total</p>
                          <p className="text-xl font-bold text-[#3f6a8e]">{user.disputedMeasuresCount}</p>
                        </div>
                        <div className="text-center px-4 py-2 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-xs text-gray-600 mb-1">Resolved</p>
                          <p className="text-xl font-bold text-[#4bb543]">{userResolvedCount}</p>
                        </div>
                        <div className="text-center px-4 py-2 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-xs text-gray-600 mb-1">Pending</p>
                          <p className="text-xl font-bold text-[#dc3545]">{userPendingCount}</p>
                        </div>
                        <ArrowRight className="w-6 h-6 text-gray-400 group-hover:text-[#84cbe9] group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <EmptyError errorMessage="No disputes found in the log" />
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
})

export default DisputeLog
